import { useNavigate } from "@solidjs/router";
import BigNumber from "bignumber.js";
import { bridgeRegistry } from "boltz-swaps/bridge";
import {
    type ChainPairTypeTaproot,
    fetchBip21Invoice,
    fetchBolt12Invoice,
} from "boltz-swaps/client";
import { isKnownTokenAddress } from "boltz-swaps/evm";
import { SwapPosition, SwapType } from "boltz-swaps/types";
import log from "loglevel";
import {
    type Accessor,
    createEffect,
    createMemo,
    createSignal,
    on,
} from "solid-js";

import {
    BTC,
    LBTC,
    LN,
    RBTC,
    getBridgeKind,
    getCanonicalAsset,
    getRouteViaAsset,
    isEvmAsset,
} from "../consts/Assets";
import { InvoiceValidation } from "../consts/Enums";
import type { ButtonLabelParams } from "../consts/Types";
import { useCreateContext } from "../context/Create";
import { useGlobalContext } from "../context/Global";
import {
    type Signer,
    customDerivationPathRdns,
    useWeb3Signer,
} from "../context/Web3";
import type { DictKey } from "../i18n/i18n";
import { GasNeededToClaim, getSmartWalletAddress } from "../rif/Signer";
import Pair, { type EncodedHop } from "../utils/Pair";
import { calculateSendAmount } from "../utils/calculate";
import { validateAddress as validateOnchainAddress } from "../utils/compat";
import {
    btcToSat,
    formatAmount,
    formatAssetAmountForLog,
    formatDenomination,
    formatSwapAmountForLog,
    miliSatToSat,
} from "../utils/denomination";
import { formatError } from "../utils/errors";
import { handleCreateSwapError } from "../utils/handleCreateSwapError";
import type { HardwareSigner } from "../utils/hardware/HardwareSigner";
import { getDestinationAddress, getPair } from "../utils/helper";
import {
    InvoiceType,
    decodeInvoice,
    fetchBip353,
    fetchLnurl,
    getAssetByBip21Prefix,
    validateInvoiceForOffer,
} from "../utils/invoice";
import { findMagicRoutingHint } from "../utils/magicRoutingHint";
import { firstResolved, promiseWithTimeout } from "../utils/promise";
import { estimateFeesPerGas } from "../utils/provider";
import { gasTopUpSupported } from "../utils/quoter";
import { canSendAsset } from "../utils/selectableAsset";
import {
    type BridgeDetail,
    type GasAbstraction,
    GasAbstractionType,
    type SomeSwap,
    createChain,
    createReverse,
    createSubmarine,
} from "../utils/swapCreator";
import { validateResponse } from "../utils/validation";
import LoadingSpinner from "./LoadingSpinner";
import { getMagicRoutingHintSavedFees } from "./OptimizedRoute";

// In milliseconds
const invoiceFetchTimeout = 25_000;

const userErrorLabelKeys = new Set<DictKey>([
    "invalid_pair",
    "invalid_send_asset",
    "maximum_amount",
    "invalid_0_amount",
    "min_amount_destination",
    "max_amount_destination",
]);

const buildBridgeDetail = (
    asset: string,
    position: SwapPosition,
): BridgeDetail | undefined => {
    const route =
        position === SwapPosition.Pre
            ? bridgeRegistry.getPreRoute(asset)
            : bridgeRegistry.getPostRoute(asset);
    if (route === undefined) {
        return undefined;
    }

    const driver = bridgeRegistry.getDriverForAsset(asset);
    if (driver === undefined) {
        return undefined;
    }

    return driver.getRoutePosition(route, position);
};

const getLockupGasAbstraction = (assetSend: string): GasAbstractionType => {
    const asset = getCanonicalAsset(assetSend);
    if (isEvmAsset(asset) && asset !== RBTC) {
        return GasAbstractionType.Signer;
    }

    return GasAbstractionType.None;
};

export const getClaimAddress = async (
    assetReceive: Accessor<string>,
    assetSend: Accessor<string>,
    signer: Accessor<Signer | undefined>,
    onchainAddress: Accessor<string>,
    getGasAbstractionSigner: (asset: string) => Signer,
    getGasToken: boolean | undefined,
): Promise<{
    gasAbstraction: GasAbstraction;
    gasPrice: bigint;
    claimAddress: string;
}> => {
    const lockupGasAbstraction = getLockupGasAbstraction(assetSend());

    if (assetReceive() === RBTC && signer() !== undefined) {
        const activeSigner = signer()!;
        const [balance, gasPrice] = await Promise.all([
            activeSigner.provider.getBalance({
                address: activeSigner.address,
            }),
            estimateFeesPerGas(activeSigner.provider).then(
                (data) => data.gasPrice ?? 0n,
            ),
        ]);
        log.debug("RSK balance", formatAssetAmountForLog(balance, RBTC));

        const balanceNeeded = gasPrice * GasNeededToClaim;
        log.debug(
            "RSK balance needed",
            formatAssetAmountForLog(balanceNeeded, RBTC),
        );

        if (balance <= balanceNeeded) {
            log.info("Using RIF smart wallet as claim address");
            return {
                gasPrice,
                gasAbstraction: {
                    lockup: lockupGasAbstraction,
                    claim: GasAbstractionType.RifRelay,
                },
                claimAddress: (await getSmartWalletAddress(activeSigner))
                    .address,
            };
        }

        log.info("RIF smart wallet not needed");
    }

    if (
        (isEvmAsset(assetReceive()) ||
            getBridgeKind(assetReceive()) !== undefined) &&
        assetReceive() !== RBTC
    ) {
        const canonicalReceiveAsset = getCanonicalAsset(assetReceive());
        const gasSigner = getGasAbstractionSigner(canonicalReceiveAsset);
        log.debug("Using gas abstraction signer", gasSigner.address);
        return {
            gasPrice: 0n,
            gasAbstraction: {
                lockup: lockupGasAbstraction,
                claim: GasAbstractionType.Signer,
            },
            claimAddress:
                getBridgeKind(assetReceive()) === undefined &&
                getRouteViaAsset(assetReceive()) === undefined &&
                !getGasToken
                    ? onchainAddress()
                    : gasSigner.address,
        };
    }

    log.debug("Using no gas abstraction");
    return {
        gasPrice: 0n,
        gasAbstraction: {
            lockup: lockupGasAbstraction,
            claim: GasAbstractionType.None,
        },
        claimAddress: onchainAddress(),
    };
};

const CreateButton = () => {
    const navigate = useNavigate();
    const {
        separator,
        setSwapStorage,
        denomination,
        pairs,
        setPairs,
        online,
        notify,
        t,
        newKey,
        deriveKey,
        rescueFile,
        regularPairs,
    } = useGlobalContext();
    const {
        pair,
        setPair,
        getGasToken,
        invoice,
        lnurl,
        onchainAddress,
        receiveAmount,
        sendAmount,
        amountValid,
        setInvoice,
        setInvoiceValid,
        setLnurl,
        setOnchainAddress,
        valid,
        addressValid,
        setAddressValid,
        minimum,
        maximum,
        invoiceValid,
        invoiceError,
        bolt12Offer,
        setBolt12Offer,
        setSendAmount,
        setReceiveAmount,
        bolt12Loading,
        quoteLoading,
        setAmountChanged,
    } = useCreateContext();
    const {
        signer,
        connectedWallet,
        providers,
        getEtherSwap,
        getErc20Swap,
        getGasAbstractionSigner,
    } = useWeb3Signer();

    const [buttonDisable, setButtonDisable] = createSignal(false);
    const [loading, setLoading] = createSignal(false);
    const [buttonLabel, setButtonLabel] = createSignal<ButtonLabelParams>({
        key: "create_swap",
    });
    const pairsLoading = () => online() && pairs() === undefined;
    const invalidPairState = () => pairs() !== undefined && !pair().isRoutable;
    const buttonClass = createMemo(() => {
        if (!online()) {
            return "btn btn-danger";
        }
        if (!pairsLoading() && userErrorLabelKeys.has(buttonLabel().key)) {
            return "btn btn-error";
        }
        return "btn";
    });
    const [originalDestination, setOriginalDestination] = createSignal<
        string | undefined
    >(undefined);

    const swapType = () => pair().swapToCreate?.type;
    const assetSend = () => pair().fromAsset;
    const assetReceive = () => pair().toAsset;
    const getSwapCreationLogContext = (
        claimAddress?: string,
        gasAbstraction?: GasAbstraction,
    ) => ({
        swapType: swapType() ?? "unknown",
        assetSend: assetSend(),
        assetReceive: assetReceive(),
        sendAmount: formatSwapAmountForLog(sendAmount(), assetSend()),
        receiveAmount: formatSwapAmountForLog(receiveAmount(), assetReceive()),
        onchainAddress: onchainAddress(),
        claimAddress,
        gasAbstraction,
        originalDestination: originalDestination(),
        getGasToken: getGasToken(),
        hasInvoice: Boolean(invoice()),
        hasLnurl: Boolean(lnurl()),
        hasBolt12Offer: Boolean(bolt12Offer()),
    });

    createEffect(
        on(
            [
                valid,
                amountValid,
                addressValid,
                invoiceValid,
                invoiceError,
                pair,
                lnurl,
                online,
                minimum,
                bolt12Offer,
                denomination,
                sendAmount,
                receiveAmount,
                onchainAddress,
                invoice,
            ],
            () => {
                setButtonDisable(false);
                if (!online()) {
                    setButtonLabel({ key: "api_offline" });
                    return;
                }
                if (pairs() === undefined) {
                    return;
                }
                if (!pair().isRoutable) {
                    if (!canSendAsset(pair().fromAsset)) {
                        setButtonLabel({ key: "invalid_send_asset" });
                    } else {
                        setButtonLabel({ key: "invalid_pair" });
                    }
                    return;
                }

                const isChainSwapWithZeroAmount = () =>
                    swapType() === SwapType.Chain &&
                    !isEvmAsset(assetSend()) &&
                    sendAmount().isZero();

                const isSubmarineSwapInvoiceValid = () =>
                    swapType() === SwapType.Submarine && !invoiceError();

                const hasInvalidDestinationInput = () => {
                    if (swapType() === SwapType.Submarine) {
                        return (
                            Boolean(invoiceError()) ||
                            (invoice() !== "" &&
                                !invoiceValid() &&
                                lnurl() === "" &&
                                bolt12Offer() === undefined)
                        );
                    }
                    return onchainAddress() !== "" && !addressValid();
                };

                const shouldShowAmountError = () =>
                    !amountValid() &&
                    // Chain swaps with 0-amount that do not have RBTC as sending asset
                    // can skip this check
                    !isChainSwapWithZeroAmount() &&
                    (isSubmarineSwapInvoiceValid() ||
                        swapType() !== SwapType.Submarine) &&
                    !(sendAmount().isZero() && hasInvalidDestinationInput());

                if (shouldShowAmountError()) {
                    if (
                        sendAmount().isGreaterThan(0) &&
                        receiveAmount().isZero()
                    ) {
                        setButtonLabel({ key: "error_zero_quote" });
                        return;
                    }

                    const lessThanMin =
                        sendAmount().isZero() ||
                        Number(sendAmount()) < minimum();
                    setButtonLabel({
                        key: lessThanMin ? "minimum_amount" : "maximum_amount",
                        params: {
                            amount: formatAmount(
                                BigNumber(lessThanMin ? minimum() : maximum()),
                                denomination(),
                                separator(),
                                assetSend(),
                            ),
                            denomination: formatDenomination(
                                denomination(),
                                assetSend(),
                            ),
                        },
                    });
                    return;
                }

                if (swapType() !== SwapType.Submarine) {
                    if (!addressValid()) {
                        setButtonLabel({
                            key: "invalid_address",
                            params: {
                                asset: assetReceive(),
                            },
                        });
                        return;
                    }
                } else {
                    if (validWayToFetchInvoice()) {
                        setButtonLabel({ key: "create_swap" });
                        return;
                    }
                    if (!invoiceValid()) {
                        setButtonLabel({
                            key: invoiceError() || "invalid_invoice",
                        });
                        return;
                    }
                }
                setButtonLabel({ key: "create_swap" });
            },
        ),
    );

    const validWayToFetchInvoice = (): boolean => {
        return (
            swapType() === SwapType.Submarine &&
            (lnurl() !== "" || bolt12Offer() !== undefined) &&
            amountValid() &&
            sendAmount().isGreaterThan(0) &&
            assetReceive() !== assetSend()
        );
    };

    const getOriginalDestination = () =>
        originalDestination() ||
        (assetReceive() !== LN && onchainAddress() !== ""
            ? onchainAddress()
            : undefined);

    const showInvalidAddress = (asset: string) => {
        setAddressValid(false);
        notify(
            "error",
            t("invalid_address", {
                asset,
            }),
        );
    };

    const fetchInvoice = async () => {
        if (lnurl() !== undefined && lnurl() !== "") {
            try {
                log.info("Fetching invoice from LNURL or BIP-353", lnurl());

                const fetched = await firstResolved(
                    [
                        (async () => {
                            try {
                                return await fetchLnurl(
                                    lnurl(),
                                    Number(receiveAmount()),
                                );
                            } catch (e) {
                                log.warn(
                                    "Fetching invoice for LNURL failed:",
                                    e,
                                );
                                if (
                                    e instanceof Error &&
                                    (
                                        Object.values(
                                            InvoiceValidation,
                                        ) as string[]
                                    ).includes(e.message)
                                ) {
                                    const satsAmount = miliSatToSat(
                                        BigNumber(e.cause as BigNumber.Value),
                                    );
                                    const value = {
                                        amount: formatAmount(
                                            BigNumber(satsAmount),
                                            denomination(),
                                            separator(),
                                            BTC,
                                        ),
                                        denomination: formatDenomination(
                                            denomination(),
                                            BTC,
                                        ),
                                    };

                                    setButtonDisable(true);

                                    const minOrMax =
                                        InvoiceValidation.MinAmount ===
                                        e.message
                                            ? "min"
                                            : "max";

                                    const errorMsg: DictKey = `${minOrMax}_amount_destination`;

                                    setButtonLabel({
                                        key: errorMsg,
                                        params: value,
                                    });

                                    throw t(errorMsg, value);
                                }
                                const hasJson =
                                    e !== null &&
                                    typeof e === "object" &&
                                    "json" in e &&
                                    typeof (e as { json: unknown }).json ===
                                        "function";
                                const error = hasJson
                                    ? await (
                                          e as { json: () => Promise<unknown> }
                                      ).json()
                                    : e;
                                throw formatError(error);
                            }
                        })(),
                        (async () => {
                            try {
                                return await fetchBip353(
                                    lnurl(),
                                    Number(receiveAmount()),
                                );
                            } catch (e) {
                                log.warn(
                                    "Fetching invoice from BIP-353 failed:",
                                    e,
                                );
                                throw formatError(e);
                            }
                        })(),
                    ].map((p) => promiseWithTimeout(p, invoiceFetchTimeout)),
                );

                setOriginalDestination(lnurl());
                setInvoice(fetched);
                setLnurl("");
                setInvoiceValid(true);
            } catch (e) {
                log.warn("Fetching invoice failed", e);
                notify("error", formatError(e));
            }
        } else {
            const offer = bolt12Offer();
            if (offer === undefined) {
                return;
            }
            log.info("Fetching invoice from bolt12 offer", offer);
            try {
                const invoice = (
                    await fetchBolt12Invoice(offer, Number(receiveAmount()))
                ).invoice;
                validateInvoiceForOffer(offer, invoice);
                setOriginalDestination(offer);
                setInvoice(invoice);
                setBolt12Offer(undefined);
                setInvoiceValid(true);
            } catch (e) {
                notify("error", formatError(e));
                log.warn("Fetching invoice from bolt12 offer failed", e);
                return;
            }
        }
    };

    const createSwap = async (
        claimAddress: string,
        gasAbstraction: GasAbstraction,
    ): Promise<boolean> => {
        try {
            let data!: SomeSwap;
            let hops!: EncodedHop[];
            let hopsPosition: SwapPosition | undefined;

            switch (swapType()) {
                case SwapType.Submarine: {
                    const createSubmarineSwap = async () => {
                        const creationData = await pair().creationData(
                            sendAmount(),
                            pair().minerFees,
                        );
                        if (creationData === undefined) {
                            throw new Error("missing swap creation data");
                        }
                        hops = creationData.hops;
                        hopsPosition = creationData.hopsPosition;
                        data = await createSubmarine(
                            creationData.from,
                            creationData.to,
                            creationData.sendAmount,
                            creationData.receiveAmount,
                            invoice(),
                            creationData.pairHash,
                            gasAbstraction,
                            newKey,
                            originalDestination(),
                        );
                    };

                    const decodedInvoice = decodeInvoice(invoice());
                    const isBolt12 = decodedInvoice.type === InvoiceType.Bolt12;

                    const magicRoutingHint = !isBolt12
                        ? findMagicRoutingHint(invoice())
                        : undefined;

                    const bip21 =
                        magicRoutingHint || isBolt12
                            ? (await fetchBip21Invoice(invoice()))?.bip21
                            : undefined;

                    const bip21Decoded = bip21 ? new URL(bip21) : undefined;

                    const bip21Asset = bip21Decoded
                        ? getAssetByBip21Prefix(bip21Decoded.protocol)
                        : undefined;

                    if (
                        !bip21 ||
                        bip21Decoded === undefined ||
                        bip21Asset === undefined ||
                        assetSend() === bip21Asset
                    ) {
                        log.debug("Creating submarine swap");
                        await createSubmarineSwap();
                        break;
                    }

                    const chainAddress = bip21Decoded.pathname;
                    const bip21Amount = BigNumber(
                        bip21Decoded.searchParams.get("amount") ?? 0,
                    );
                    const bip21AmountSats = btcToSat(bip21Amount);

                    try {
                        // Create swap using its Magic Routing Hint (MRH)
                        log.debug("MRH detected. Preparing swap");

                        // If bip21Amount is less than the minimal for the new pair, don't use the MRH
                        const chainPair = getPair<ChainPairTypeTaproot>(
                            pairs(),
                            SwapType.Chain,
                            assetSend(),
                            bip21Asset,
                        );
                        if (
                            !chainPair ||
                            bip21AmountSats.isLessThan(chainPair.limits.minimal)
                        ) {
                            log.debug(
                                `BIP21 amount ${formatSwapAmountForLog(
                                    bip21AmountSats,
                                    bip21Asset,
                                )} is less than minimal ${
                                    chainPair === undefined
                                        ? "unknown"
                                        : formatSwapAmountForLog(
                                              BigNumber(
                                                  chainPair.limits.minimal,
                                              ),
                                              bip21Asset,
                                          )
                                } for chain swap. Creating submarine swap.`,
                            );
                            await createSubmarineSwap();
                            break;
                        }

                        if (
                            bip21AmountSats.isGreaterThan(
                                decodedInvoice.satoshis,
                            )
                        ) {
                            throw new Error("invalid_bip21_amount");
                        }

                        const mrhSendAmount = calculateSendAmount(
                            bip21AmountSats,
                            chainPair.fees.percentage,
                            chainPair.fees.minerFees.server +
                                chainPair.fees.minerFees.user.claim,
                            SwapType.Chain,
                        );

                        const savedFees = getMagicRoutingHintSavedFees({
                            pairs,
                            assetSend,
                            addressValid,
                            onchainAddress,
                            sendAmount: () => mrhSendAmount,
                            assetReceive: () => bip21Asset,
                        });

                        if (BigNumber(savedFees).isLessThanOrEqualTo(0)) {
                            log.debug(
                                "MRH is more expensive than submarine swap. Creating submarine swap",
                            );
                            await createSubmarineSwap();
                            break;
                        }

                        setPair(
                            new Pair(
                                pairs(),
                                assetSend(),
                                bip21Asset,
                                regularPairs(),
                            ),
                        );
                        setOnchainAddress(chainAddress);
                        setReceiveAmount(bip21AmountSats);
                        setSendAmount(mrhSendAmount);

                        log.debug("Creating MRH swap");
                        const mrhRescue = rescueFile();
                        if (mrhRescue === null) {
                            throw new Error("missing rescue file");
                        }
                        const chainSwap = await createChain(
                            assetSend(),
                            bip21Asset,
                            sendAmount(),
                            receiveAmount(),
                            onchainAddress(),
                            chainPair.hash,
                            gasAbstraction,
                            mrhRescue,
                            newKey,
                            originalDestination(),
                        );

                        data = {
                            ...chainSwap,
                            magicRoutingHintSavedFees: savedFees,
                        };

                        break;
                    } catch (e) {
                        log.error("Error creating MRH swap", {
                            ...getSwapCreationLogContext(
                                claimAddress,
                                gasAbstraction,
                            ),
                            bip21Asset,
                            chainAddress,
                            bip21Amount: formatSwapAmountForLog(
                                bip21AmountSats,
                                bip21Asset,
                            ),
                            error: formatError(e),
                        });
                        throw new Error(t("invalid_invoice"), { cause: e });
                    }
                }

                case SwapType.Reverse: {
                    const creationData = await pair().creationData(
                        sendAmount(),
                        pair().minerFees,
                    );
                    if (creationData === undefined) {
                        throw new Error("missing swap creation data");
                    }
                    const rescue = rescueFile();
                    if (rescue === null) {
                        throw new Error("missing rescue file");
                    }
                    hops = creationData.hops;
                    hopsPosition = creationData.hopsPosition;
                    data = await createReverse(
                        creationData.from,
                        creationData.to,
                        creationData.sendAmount,
                        creationData.receiveAmount,
                        claimAddress,
                        creationData.pairHash,
                        gasAbstraction,
                        rescue,
                        newKey,
                        getOriginalDestination(),
                    );
                    break;
                }

                case SwapType.Chain: {
                    const creationData = await pair().creationData(
                        sendAmount(),
                        pair().minerFees,
                    );
                    if (creationData === undefined) {
                        throw new Error("missing swap creation data");
                    }
                    const rescue = rescueFile();
                    if (rescue === null) {
                        throw new Error("missing rescue file");
                    }
                    hops = creationData.hops;
                    hopsPosition = creationData.hopsPosition;
                    data = await createChain(
                        creationData.from,
                        creationData.to,
                        creationData.sendAmount,
                        creationData.receiveAmount,
                        claimAddress,
                        creationData.pairHash,
                        gasAbstraction,
                        rescue,
                        newKey,
                        getOriginalDestination(),
                    );
                    break;
                }
            }

            try {
                await validateResponse(
                    data,
                    deriveKey,
                    getEtherSwap,
                    getErc20Swap,
                );
            } catch (e) {
                const error = e instanceof Error ? e : new Error(String(e));
                log.error(
                    `failed to create ${swapType()} swap: ${error.stack}`,
                );
                log.error("server response for swap creation:", data);
                navigate("/error");
                return false;
            }

            log.debug(`Created swap ${data.id}:`, {
                destination: getDestinationAddress(data),
                receiveAmount: formatSwapAmountForLog(
                    data.receiveAmount,
                    data.assetReceive,
                ),
            });

            const bridge =
                buildBridgeDetail(assetSend(), SwapPosition.Pre) ??
                buildBridgeDetail(assetReceive(), SwapPosition.Post);

            await setSwapStorage({
                ...data,
                getGasToken: getGasToken(),
                dex:
                    hopsPosition !== undefined
                        ? {
                              hops,
                              position: hopsPosition,
                              quoteAmount:
                                  hopsPosition === SwapPosition.Post
                                      ? Number(receiveAmount())
                                      : Number(sendAmount()),
                          }
                        : undefined,
                bridge,
                signer:
                    // We do not have to commit to a signer when creating submarine swaps
                    swapType() !== SwapType.Submarine
                        ? (signer()?.address ?? connectedWallet()?.address)
                        : undefined,
                derivationPath:
                    swapType() !== SwapType.Submarine &&
                    signer() !== undefined &&
                    customDerivationPathRdns.includes(signer()!.rdns)
                        ? (
                              providers()[signer()!.rdns]
                                  .provider as unknown as HardwareSigner
                          ).getDerivationPath()
                        : undefined,
            });

            setInvoice("");
            setInvoiceValid(false);
            setOnchainAddress("");
            setAddressValid(false);
            setOriginalDestination(undefined);

            navigate("/swap/" + data.id);

            return true;
        } catch (err) {
            log.error("Swap creation failed", {
                ...getSwapCreationLogContext(claimAddress, gasAbstraction),
                error: formatError(err),
            });

            const recovered = await handleCreateSwapError(
                err,
                notify,
                t,
                pair,
                regularPairs,
                setPairs,
                setSendAmount,
                setAmountChanged,
            );

            if (!recovered) {
                notify("error", formatError(err));
            }

            return false;
        }
    };

    const buttonClick = async () => {
        setLoading(true);
        try {
            if (validWayToFetchInvoice()) {
                await fetchInvoice();
            }

            const { gasAbstraction, claimAddress } = await getClaimAddress(
                assetReceive,
                assetSend,
                signer,
                onchainAddress,
                getGasAbstractionSigner,
                getGasToken(),
            );

            if (isKnownTokenAddress(assetReceive(), onchainAddress())) {
                showInvalidAddress(assetReceive());
                return;
            }

            if (
                (assetReceive() === BTC || assetReceive() === LBTC) &&
                !validateOnchainAddress(assetReceive(), claimAddress)
            ) {
                showInvalidAddress(assetReceive());
                return;
            }

            if (!valid()) return;

            log.debug("Creating with EVM address", claimAddress);

            await createSwap(claimAddress, gasAbstraction);
        } catch (e) {
            log.error("Swap creation setup failed", {
                ...getSwapCreationLogContext(),
                error: formatError(e),
            });
            notify("error", formatError(e));
        } finally {
            setLoading(false);
        }
    };

    const getButtonLabel = (label: ButtonLabelParams) => {
        return t(label.key, label.params);
    };

    return (
        <button
            id="create-swap-button"
            data-testid="create-swap-button"
            class={buttonClass()}
            disabled={
                !online() ||
                pairsLoading() ||
                !(valid() || validWayToFetchInvoice()) ||
                buttonDisable() ||
                loading() ||
                quoteLoading() ||
                (gasTopUpSupported(assetReceive()) &&
                    getGasToken() === undefined) ||
                (onchainAddress() === "" &&
                    invoice() === "" &&
                    bolt12Offer() === undefined &&
                    lnurl() === "")
            }
            onClick={buttonClick}>
            {(pairsLoading() ||
                loading() ||
                bolt12Loading() ||
                quoteLoading()) &&
            !invalidPairState() ? (
                <LoadingSpinner class="inner-spinner" />
            ) : (
                getButtonLabel(buttonLabel())
            )}
        </button>
    );
};

export default CreateButton;
